import { Field } from '@headlessui/react';
import React, { FC, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import MinusIcon from '../../../../../../assets/icons/minus-icon';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { Checkbox } from '../../../../../../components/checkbox/index';
import Loader from '../../../../../../components/loader/index';
import { PaginationController } from '../../../../../../components/pagination-controller/index';
import { Radio, RadioGroup } from '../../../../../../components/radio-group/index';
import { Switch } from '../../../../../../components/switch/index';
import { QueryKey } from '../../../../../../constants/index';
import { ScenarioHooks, useInvalidateQueries, usePagination } from '../../../../../../hooks/index';
import { GrantItemFilter, ScenarioType } from '../../../../../../types/scenario-modeling.types';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { FundraisingFormSchema } from '../../../fundraising-validation';
import { LiquidityFormSchema } from '../../../liquidity-validation';
import { valuationFormSchema } from '../../../scenario-details.validation';
import { TableHeaderItem } from '../form-components/index';
import { useGrantStore } from '../grant.store';
import { GrantItem } from './grant-item';
import { ExistingItemListProps, itemsToFetchMap } from './items.types';

type FormSchema = FundraisingFormSchema | LiquidityFormSchema;

export const CommonGrantItemList: FC<ExistingItemListProps> = ({
  control,
  scenarioId,
  columns,
  additionalColumns,
  scenarioType,
}) => {
  const { grants, valuation } = useWatch<FormSchema>({ control });
  const [currentPage, setCurrentPage] = usePagination(`scenario-grants:${scenarioId}`);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [selectedFilter, setSelectedFilter] = useState<GrantItemFilter>(GrantItemFilter.GRANTS);
  const parsedValuation = valuationFormSchema.safeParse(valuation);
  const isValidValuation = Boolean(
    parsedValuation.success && parsedValuation.data.date && parsedValuation.data.preMoneyValuation,
  );
  const { deselectAll, selectAll, scenarioStates } = useGrantStore();
  const grantsStore = scenarioStates[scenarioId] ?? null;
  const { scenarioVestingTasks, totalPages, totalRecords } = ScenarioHooks.useScenarioVestingTasks({
    scenarioId,
    numberOfFetchedItems: Number(itemsToFetch),
    currentPage,
  });
  const { scheduleUpdate } = useScenarioCapTableStore();
  const { scenarioGrants } = ScenarioHooks.useScenarioGrants({
    scenarioId,
  });

  const { pointerBlockedStyles } = useScenarioStore();

  const scenarioVestingSars = undefined;
  const sarTotalPages = 0;

  const allGrants = [...(scenarioVestingTasks || []), ...(scenarioVestingSars || [])];

  const filteredItems =
    selectedFilter === GrantItemFilter.GRANTS ? scenarioVestingTasks : scenarioVestingSars;

  const { deleteScenarioGrants, isPending: isPendingDeletion } =
    ScenarioHooks.useDeleteAllScenarioGrants();
  const { selectAllGrants, isPending } = ScenarioHooks.useSelectAllScenarioGrants();
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_SCENARIO_VESTING_TASKS,
    QueryKey.GET_SCENARIO,
  );

  const initialSelectedIds = useMemo(() => {
    return Object.values(grants?.grantItems ?? {})
      .map((item) => item?.vestingTaskId)
      .filter((grantId): grantId is string => Boolean(grantId));
  }, [grants?.grantItems]);

  const onSelectAll = () => {
    selectAll(scenarioId, totalRecords || 0);
    scheduleUpdate(scenarioId);
    if (selectedFilter === GrantItemFilter.GRANTS) {
      selectAllGrants(
        { scenarioId },
        {
          onSuccess: () => {
            invalidateQuery();
          },
        },
      );
    } else {
      selectAllGrants({ scenarioId, sarOnly: true }, { onSuccess: () => invalidateQuery() });
    }
  };
  const onRemoveSelectAll = () => {
    deselectAll(scenarioId);
    scheduleUpdate(scenarioId);
    if (selectedFilter === GrantItemFilter.GRANTS) {
      deleteScenarioGrants(
        { scenarioId },
        {
          onSuccess: () => {
            invalidateQuery();
          },
        },
      );
    } else {
      deleteScenarioGrants({ scenarioId, sarOnly: true }, { onSuccess: () => invalidateQuery() });
    }
  };

  const exercise = scenarioGrants && scenarioGrants.length === totalRecords;

  return (
    <div
      className={twMerge(
        'relative flex w-full min-w-[1520px] flex-col items-end gap-3',
        isPending || isPendingDeletion ? 'pointer-events-none opacity-50' : '',
      )}
    >
      {(isPending || isPendingDeletion) && <Loader className="absolute z-10" />}
      {filteredItems && filteredItems.length > 0 && (
        <>
          <div className="flex w-full items-center justify-between gap-4">
            <div className="sticky left-0">
              <RadioGroup
                className="h-fit w-fit divide-x divide-gray-200 rounded border border-gray-200"
                onChange={setSelectedFilter}
                value={selectedFilter}
              >
                {scenarioVestingTasks && scenarioVestingTasks?.length > 0 && (
                  <Radio
                    className="flex h-9 w-full items-center justify-center px-4 text-sm font-[450] text-[#858593] data-[checked]:bg-brand-25 data-[checked]:font-[550] data-[checked]:text-[#172335]"
                    disabled={allGrants && allGrants.length === 0}
                    onCheckedDisableClassName="data-[checked]:bg-brand-25 data-[checked]:text-gray-300 cursor-default"
                    styleType="NONE"
                    value={GrantItemFilter.GRANTS}
                  >
                    <span
                      className={twMerge(
                        'overflow-hidden text-ellipsis whitespace-nowrap',
                        allGrants && allGrants.length === 0 ? 'text-gray-300' : 'text-gray-700',
                      )}
                    >
                      <AppFormattedMessage id="GRANTS" />
                    </span>
                  </Radio>
                )}
              </RadioGroup>
            </div>
            <div className="sticky right-0 rounded-md bg-gray-50">
              <div className={twJoin('flex items-center gap-2 px-4 py-3', pointerBlockedStyles)}>
                <Switch
                  checked={grantsStore?.isSelectedAll || exercise}
                  disabled={!isValidValuation}
                  enabled={exercise}
                  onChange={(state) => {
                    state ? onSelectAll() : onRemoveSelectAll();
                  }}
                />
                <span className="text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id="EXERCISE_ALL" />
                </span>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              'flex w-full min-w-[1520px] flex-col rounded-lg border-[1px] border-gray-100 bg-white',
              selectedFilter === GrantItemFilter.GRANTS ? 'min-w-[1380px]' : 'min-w-[1500px]',
            )}
          >
            <div
              className="grid"
              style={{
                gridTemplateColumns: `48px repeat(${scenarioType === ScenarioType.LIQUIDITY ? 8 : 7}, minmax(140px, 1fr)) 190px`,
              }}
            >
              <div
                className={twJoin(
                  'relative flex h-full w-full items-center justify-center border-b-[1px] border-b-gray-100 bg-gray-50 px-4',
                  pointerBlockedStyles,
                )}
              >
                <Field className="flex cursor-pointer items-center">
                  <Checkbox
                    checked={Boolean(grantsStore.selectedItemsCount)}
                    id="select-all"
                    onChange={(checked) => (checked ? onSelectAll() : onRemoveSelectAll())}
                    selectIcon={<MinusIcon />}
                  />
                </Field>
              </div>
              {(selectedFilter === GrantItemFilter.GRANTS ? columns : additionalColumns)?.map(
                (column, index) => (
                  <TableHeaderItem key={index} text={column} />
                ),
              )}
            </div>
            {filteredItems.map((item) => (
              <div
                className="grid border-b-[1px] border-b-gray-100"
                key={item.id}
                style={{
                  gridTemplateColumns: `48px repeat(${scenarioType === ScenarioType.LIQUIDITY ? 8 : 7}, minmax(140px, 1fr)) 190px`,
                }}
              >
                <GrantItem
                  control={control}
                  isChecked={
                    grantsStore?.isSelectedAll ||
                    !!initialSelectedIds.find((selectedId) => selectedId === item.id)
                  }
                  item={item}
                  scenarioId={scenarioId}
                  scenarioType={scenarioType}
                />
              </div>
            ))}
          </div>
        </>
      )}
      <PaginationController
        className="sticky right-0"
        currentPage={currentPage}
        itemsToFetch={itemsToFetch}
        itemsToFetchMap={itemsToFetchMap}
        onClick={setCurrentPage}
        onFetchAmountChange={setItemsToFetch}
        totalPages={
          selectedFilter === GrantItemFilter.GRANTS ? totalPages || 1 : sarTotalPages || 1
        }
      />
    </div>
  );
};
